import { EventType } from "@/analytics/analytics-events";
import { ChangeWaypointOrderProperties } from "@/analytics/data-organization-events-properties";
import { loadArea } from "@/components/common/project-provider/project-loading-context";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import {
  addIElementsToStore,
  moveElementInStore,
} from "@/store/ielements-store-utilities";
import { selectLightweightChild } from "@/store/lightweight-elements-selectors";
import { LightweightElement } from "@/store/lightweight-elements-slice";
import { useAppDispatch, useAppStore } from "@/store/store-hooks";
import { TreeData, TreeDataElement } from "@/utils/tree-generation/tree-data";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  IElementType,
  IElementTypeHint,
  isIElementAreaSection,
  isIElementWithTypeAndHint,
} from "@faro-lotv/ielement-types";
import {
  addToLoadingRoots,
  removeFromLoadingRoots,
  selectChildDepthFirst,
  selectIElement,
} from "@faro-lotv/project-source";
import {
  createMutationMoveRoomToArea,
  createMutationSetElementOrder,
  useApiClientContext,
} from "@faro-lotv/service-wires";
import { useCallback, useState } from "react";
import { MoveHandler, NodeApi } from "react-arborist";
import { getRoomsGroupToCreateIfNeeded } from "../tree-context-menu/actions/move-waypoint-action";
import { selectIsAreaLoaded } from "../tree-context-menu/utils";
import { isAreaSection, isRoomSection } from "./capture-tree-discriminator";

/**
 * @returns The callback executed when an element is dragged and dropped in the capture tree
 * @param tree The tree structured displayed in the capture tree
 */
export function useCaptureTreeMoveHandler(
  tree: TreeData[],
): MoveHandler<TreeData> {
  const { projectApiClient } = useApiClientContext();
  const dispatch = useAppDispatch();
  const { handleErrorWithToast } = useErrorHandlers();

  const { getState } = useAppStore();
  const moveElement = useCallback(
    async (args: {
      dragIds: string[];
      dragNodes: Array<NodeApi<TreeData>>;
      parentId: string | null;
      parentNode: NodeApi<TreeData> | null;
      index: number;
    }): Promise<void> => {
      const { dragNodes, parentNode, index } = args;
      const draggedNode = dragNodes[0];
      const draggedElement = draggedNode.data.element;
      if (!draggedElement) return;

      if (isRoomSection(draggedElement)) {
        const roomId = draggedElement.id;

        // Find the elements that are necessary to properly compute the new project structure
        const {
          targetAreaNode,
          currentAreaNode,
          nextSibling,
          previousSibling,
        } = dropRoom(draggedNode, parentNode, index, tree);

        if (!targetAreaNode || !currentAreaNode) {
          return;
        }

        // Do nothing if the iElement didn't really change its position
        if (nextSibling?.id === roomId || previousSibling?.id === roomId) {
          return;
        }

        // Find the target area where the element is being dropped
        const targetAreaElement = selectIElement(targetAreaNode.element?.id)(
          getState(),
        );
        assert(
          targetAreaElement && isIElementAreaSection(targetAreaElement),
          "Cannot find the area target of a room move",
        );

        const isTargetAreaLoaded = selectIsAreaLoaded(
          targetAreaElement,
          getState(),
        );

        // Check if the target area already contains a rooms group or if it must be created
        // If groupNode is not defined by a rooms group exists, we load it from the backend
        const groupToCreate = await getRoomsGroupToCreateIfNeeded(
          targetAreaElement,
          projectApiClient,
        );

        // The target rooms could be a new one that we need to create or it can already exist and has been
        // loaded by the getRoomsGroupToCreateIfNeeded function
        const groupId = groupToCreate
          ? groupToCreate.id
          : selectChildDepthFirst(targetAreaElement, (e) =>
              isIElementWithTypeAndHint(
                e,
                IElementType.group,
                IElementTypeHint.rooms,
              ),
            )(getState())?.id;
        assert(groupId, "Cannot find group ID where to move the room");

        // If a group already exists in the local store, we can move it locally
        if (isTargetAreaLoaded) {
          moveElementInStore(
            {
              id: roomId,
              newParentId: groupId,
              nextSibling: nextSibling?.id,
            },
            dispatch,
          );
        }

        // Get the group in the current area
        const currentAreaElement = selectIElement(currentAreaNode.element?.id)(
          getState(),
        );
        assert(
          currentAreaElement && isIElementAreaSection(currentAreaElement),
          "Cannot find the area where the room was before the move",
        );
        const currentGroupElement =
          selectChildDepthFirst(currentAreaElement, (e) =>
            isIElementWithTypeAndHint(
              e,
              IElementType.group,
              IElementTypeHint.rooms,
            ),
          )(getState()) ??
          selectLightweightChild(
            currentAreaElement,
            (e): e is LightweightElement =>
              e.type === IElementType.group &&
              e.typeHint === IElementTypeHint.rooms,
          )(getState());
        assert(
          currentGroupElement,
          "Cannot find the group where the room was before the move",
        );

        // Create the mutation to move the area to the new position
        const mutation = createMutationMoveRoomToArea(
          roomId,
          targetAreaElement.id,
          groupToCreate,
          nextSibling?.id,
        );
        try {
          await projectApiClient.applyMutations([mutation]);
          Analytics.track<ChangeWaypointOrderProperties>(
            EventType.changeWaypointOrder,
            {
              isMovedToDifferentArea: currentAreaNode.id !== targetAreaNode.id,
            },
          );
          // If the target rooms group was not loaded when we performed the drag and drop,
          // we don't move the element locally, but we fetch it from the backend
          if (!isTargetAreaLoaded) {
            dispatch(addToLoadingRoots(targetAreaElement.id));
            const newElements = await projectApiClient.getAllIElements({
              descendantIds: [currentGroupElement.id],
            });
            addIElementsToStore(newElements, dispatch);
            await loadArea(targetAreaElement.id, projectApiClient, dispatch);
            dispatch(removeFromLoadingRoots(targetAreaElement.id));
          } else if (!selectIElement(currentGroupElement.id)(getState())) {
            // In this case, a room from a not loaded area has been moved to another area
            const roomAncestors = await projectApiClient.getAllIElements({
              descendantIds: [roomId],
            });
            const roomChildren = await projectApiClient.getAllIElements({
              ancestorIds: [roomId],
            });
            addIElementsToStore([...roomAncestors, ...roomChildren], dispatch);
          }
          draggedNode.tree.get(targetAreaNode.id)?.open();
        } catch (error) {
          handleErrorWithToast({
            title: `${draggedNode.data.label} cannot be moved`,
            error,
          });
          // Sync with the Project API, so if the mutation failed,
          // the user still has a valid project structure
          const iElements = await projectApiClient.getAllIElements({
            descendantIds: [roomId, groupId, currentGroupElement.id],
          });
          addIElementsToStore(iElements, dispatch);
        }
      } else if (isAreaSection(draggedElement)) {
        const siblings = parentNode?.data.children;
        const nextSibling = siblings ? siblings.at(index) : tree.at(index);

        const areaId = draggedElement.id;
        if (!areaId) {
          return;
        }

        // Do nothing if the iElement didn't really change its position
        if (
          nextSibling?.id === areaId ||
          parentNode?.children?.[index - 1]?.id === areaId
        ) {
          return;
        }

        // Send the mutation and update the local store at the same time
        moveElementInStore(
          {
            id: areaId,
            nextSibling: nextSibling?.id,
          },
          dispatch,
        );

        const mutation = createMutationSetElementOrder(areaId, nextSibling?.id);
        try {
          await projectApiClient.applyMutations([mutation]);
          Analytics.track(EventType.changeAreaOrder);
        } catch (error) {
          handleErrorWithToast({
            title: "Failed to change the order",
            error,
          });
          // Sync with the Project API, so if the mutation failed,
          // the user still has a valid project structure
          const iElements = await projectApiClient.getAllIElements({
            descendantIds: [areaId],
          });
          addIElementsToStore(iElements, dispatch);
        }
      }
    },
    [tree, getState, projectApiClient, dispatch, handleErrorWithToast],
  );

  const [isMutationRunning, setIsMutationRunning] = useState(false);
  return useCallback<MoveHandler<TreeData>>(
    async (args) => {
      const draggedNode = args.dragNodes.at(0);
      if (!draggedNode) return;

      if (isMutationRunning) {
        handleErrorWithToast({
          title: `${draggedNode.data.label} cannot be moved`,
          error: "Unable to move while another operation is in progress",
        });
        return;
      }

      const draggedElementId = draggedNode.data.element?.id;
      if (!draggedElementId) return;

      dispatch(addToLoadingRoots(draggedElementId));
      setIsMutationRunning(true);
      try {
        await moveElement(args);
      } finally {
        setIsMutationRunning(false);
      }
      dispatch(removeFromLoadingRoots(draggedElementId));
    },
    [dispatch, handleErrorWithToast, isMutationRunning, moveElement],
  );
}

type RoomDropNodes = {
  /** The target area for the drop */
  targetAreaNode?: TreeData;
  /** The area from which the drag operation started */
  currentAreaNode?: TreeData;
  /** The next sibling in the target area */
  nextSibling?: TreeData;
  /** The previous sibling in the target area */
  previousSibling?: TreeData;
};

/**
 * @returns Collect the necessary nodes for the drag and drop of rooms
 * @param draggedNode The node being dragged
 * @param targetNode The target parent receiving the drop action
 * @param index The index at which the element was dropped
 * @param tree The data used to populate the tree
 */
function dropRoom(
  draggedNode: NodeApi<TreeData> | null,
  targetNode: NodeApi<TreeData> | null,
  index: number,
  tree: TreeData[],
): RoomDropNodes {
  const areaNode = targetNode?.data ?? tree.at(index - 1);
  if (!areaNode) return {};

  const siblings = areaNode.children;
  const nextSibling = siblings ? siblings.at(index) : tree.at(index);
  const previousSibling = siblings
    ? siblings.at(index - 1)
    : tree.at(index - 1);

  const currentAreaNode = findParent(draggedNode, isAreaSection)?.data;

  return {
    currentAreaNode,
    targetAreaNode: areaNode,
    nextSibling: targetNode ? nextSibling : undefined,
    previousSibling,
  };
}

/**
 * @returns The parent node element that matches the type guard
 * @param node The node  to check
 * @param typeGuard The function used to specify the type of node that should be found
 */
function findParent(
  node: NodeApi<TreeData> | null,
  typeGuard: (element: TreeDataElement | undefined) => boolean,
): NodeApi<TreeData> | null {
  // Find the group node where the room was before the drag started
  let currentNode: NodeApi<TreeData> | null = node;
  while (currentNode) {
    if (typeGuard(currentNode.data.element)) {
      return currentNode;
    }
    currentNode = currentNode.parent;
  }
  return null;
}
